import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Funnel, X } from "@phosphor-icons/react";
import AIToolCard from "@/components/AIToolCard";
import SEO from "@/components/SEO";
import { fetchAISection } from "@/lib/api";

const CATEGORIES_LIST = [
  "All", "Content", "SEO", "Social", "Ads", "Design", "Analytics", "Automation",
];

const SORT_OPTIONS = [
  { value: "hot",  label: "Trend Score" },
  { value: "new",  label: "New First"   },
  { value: "name", label: "Name (A–Z)"  },
];

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
      <div className="flex gap-3 mb-3">
        <div className="w-10 h-10 bg-slate-200 rounded-xl shrink-0" />
        <div className="flex-1">
          <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
          <div className="h-3 bg-slate-200 rounded w-1/3" />
        </div>
      </div>
      <div className="h-3 bg-slate-200 rounded w-full mb-2" />
      <div className="h-3 bg-slate-200 rounded w-2/3" />
    </div>
  );
}

export default function AIToolsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const categoryParam = searchParams.get("category") || "all";
  const sortParam = searchParams.get("sort") || "hot";

  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState(
    CATEGORIES_LIST.find((c) => c.toLowerCase() === categoryParam) || "All"
  );
  const [sort, setSort] = useState(
    SORT_OPTIONS.find((o) => o.value === sortParam) ? sortParam : "hot"
  );

  const [allTools, setAllTools] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchAISection("tools", { page: 1, page_size: 100, sort: "hot" })
      .then((r) => setAllTools(Array.isArray(r) ? r : (r?.items ?? [])))
      .catch(() => setError("Could not load tools — check back shortly."))
      .finally(() => setLoading(false));
  }, []);

  const updateParams = (newCat, newSort) => {
    const params = {};
    if (newCat && newCat !== "All") params.category = newCat.toLowerCase();
    if (newSort && newSort !== "hot") params.sort = newSort;
    setSearchParams(params, { replace: true });
  };

  const handleCategoryClick = (cat) => {
    setCategory(cat);
    updateParams(cat, sort);
  };

  const handleSortChange = (s) => {
    setSort(s);
    updateParams(category, s);
  };

  const filtered = allTools
    .filter((t) => {
      const matchCat = category === "All" || (t.category || "").toLowerCase() === category.toLowerCase();
      const matchSearch = search === "" ||
        (t.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (t.description || "").toLowerCase().includes(search.toLowerCase()) ||
        (t.category || "").toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    })
    .sort((a, b) => {
      if (sort === "name") return (a.name || "").localeCompare(b.name || "");
      if (sort === "new")  return ((b.isNew || b.is_new) ? 1 : 0) - ((a.isNew || a.is_new) ? 1 : 0) || (b.score || 0) - (a.score || 0);
      return (b.score || 0) - (a.score || 0);
    });

  const hasFilters = search !== "" || category !== "All";

  return (
    <>
      <SEO
        title="AI Tools — TrendMing"
        description="Discover and compare the latest AI tools, curated by TrendMing. Free to browse, no paywall."
        canonical="https://trendming.com/ai-tools"
      />

      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-green-600">
            / AI Tools Hub
          </span>
          <h1 className="font-bold text-4xl md:text-5xl text-slate-900 mt-2">
            AI tools for marketing freelancers &amp; agencies
          </h1>
          <p className="mt-4 text-slate-500 text-lg max-w-2xl leading-relaxed">
            {loading
              ? "Fetching the latest AI tools…"
              : `${allTools.length}+ tools curated for content, SEO, social, ads, and design.`}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tools…"
                aria-label="Search AI tools"
                className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
              />
            </div>
            <div className="relative">
              <Funnel size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
              <select
                value={sort}
                onChange={(e) => handleSortChange(e.target.value)}
                aria-label="Sort tools by"
                className="pl-8 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 appearance-none cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-10">
        {/* Category filter tabs — each sets ?category= param for SEO indexability */}
        <div className="flex flex-wrap gap-2 mb-8" role="group" aria-label="Filter by category">
          {CATEGORIES_LIST.map((cat) => (
            <a
              key={cat}
              href={cat === "All" ? "/ai-tools" : `/ai-tools?category=${cat.toLowerCase()}`}
              onClick={(e) => { e.preventDefault(); handleCategoryClick(cat); }}
              aria-pressed={category === cat}
              className={`px-4 py-2 text-xs font-semibold rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30 min-h-[36px] inline-flex items-center cursor-pointer ${
                category === cat
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "bg-slate-100 border-slate-100 text-slate-600 hover:bg-slate-200 hover:border-slate-200"
              }`}
            >
              {cat}
            </a>
          ))}
          {hasFilters && (
            <button
              onClick={() => { setSearch(""); handleCategoryClick("All"); }}
              className="inline-flex items-center gap-1 px-4 py-2 text-xs font-semibold rounded-full border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30 min-h-[36px]"
            >
              <X size={12} aria-hidden="true" /> Clear
            </button>
          )}
        </div>

        {!loading && !error && (
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-6" aria-live="polite" aria-atomic="true">
            {filtered.length} tool{filtered.length !== 1 ? "s" : ""}
            {category !== "All" ? ` in ${category}` : ""}
            {search ? ` matching "${search}"` : ""}
          </p>
        )}

        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {error && !loading && (
          <div className="py-20 text-center border border-dashed border-red-200 rounded-xl">
            <p className="text-red-500 font-medium">{error}</p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((tool) => (
              <AIToolCard key={tool.id || tool.slug} tool={tool} />
            ))}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && allTools.length > 0 && (
          <div className="py-20 text-center border border-dashed border-slate-300 rounded-xl">
            <p className="text-slate-500 font-medium">No tools match your search.</p>
            <button
              onClick={() => { setSearch(""); handleCategoryClick("All"); }}
              className="mt-3 text-sm text-green-600 hover:underline focus:outline-none focus:underline"
            >
              Clear filters
            </button>
          </div>
        )}

        {!loading && !error && allTools.length === 0 && (
          <div className="py-20 text-center border border-dashed border-slate-300 rounded-xl">
            <p className="text-slate-500 font-medium">Tools are being collected — check back shortly.</p>
          </div>
        )}
      </div>
    </>
  );
}
